// =================================================================
// PlusMenu.js — Menu "+" unifié zone de saisie (Session 17)
// Bouton "+" à gauche du textarea, popover contextuel avec sections :
// Fichiers, Projet, Connecteurs, Outils
// =================================================================

import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import {
  Plus, X,
  Paperclip, ClipboardPaste, Camera,
  Link, StickyNote,
  MessageCircle, Mail, HardDrive, Github, Zap,
  Globe, Layout, FolderTree, Database
} from 'lucide-react';
import ConnectorActivationModal from '../connectors/ConnectorActivationModal';
import MailInbox from '../connectors/MailInbox';

const PlusMenu = ({
  onFilesSelected,
  onPasteContent,
  onScreenCapture,
  onLinkFile,
  onCreateNote,
  onTriggerN8N,
  onInsertTemplate,
  onAnalyzeUrl,
  onOrganizeFiles,
  isProjectContext = false,
  disabled = false,
  connectors = [],
  projectId = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const [apiConnectors, setApiConnectors] = useState([]);
  // S7 — Modale activation connecteur ({ code, name }) ou null
  const [activatingConnector, setActivatingConnector] = useState(null);
  // P7 — Boîte mail plein écran ({ code, name }) ou null
  const [inboxConnector, setInboxConnector] = useState(null);
  const menuRef = useRef(null);
  const fileInputRef = useRef(null);

  // Charger les connecteurs — catalogue global + état par projet si projectId fourni
  // S7 : la route principale `/api/connectors` retourne le catalogue plat (rétrocompat S17).
  // S2 : si on est en contexte projet, on enrichit avec `/api/connectors/projects/:id`
  //       pour savoir lesquels sont déjà activés.
  // refreshTick : incrémenté après chaque activation pour relancer le fetch et mettre
  // à jour le badge "Actif" sans recharger la page.
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    const fetchConnectors = async () => {
      try {
        const token = localStorage.getItem('oracle_token');
        const headers = { Authorization: `Bearer ${token}` };

        const catalogRes = await axios.get('/api/connectors', { headers });
        const catalog = catalogRes.data?.connectors || [];

        if (isProjectContext && projectId) {
          try {
            const projRes = await axios.get(`/api/connectors/projects/${projectId}`, { headers });
            const activated = projRes.data?.connectors || [];
            const activatedMap = {};
            activated.forEach(c => { activatedMap[c.connector_code] = c; });
            // Enrichit le catalogue avec un flag `activated`
            const enriched = catalog.map(c => ({
              ...c,
              activated: Boolean(activatedMap[c.type]),
              activated_state: activatedMap[c.type] || null,
            }));
            setApiConnectors(enriched);
            return;
          } catch {
            // Si on n'a pas accès au projet ou erreur, on retombe sur le catalogue brut
          }
        }
        setApiConnectors(catalog);
      } catch (err) {
        // Silencieux — on utilise les defaults
      }
    };
    fetchConnectors();
  }, [isProjectContext, projectId, refreshTick]);

  // Fermer au clic extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Fermer avec Escape
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [isOpen]);

  const handleFileClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
    setIsOpen(false);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      onFilesSelected(e.target.files);
      e.target.value = ''; // Reset pour permettre re-sélection du même fichier
    }
  };

  const handleScreenCapture = async () => {
    setIsOpen(false);
    if (onScreenCapture) {
      onScreenCapture();
    }
  };

  const handleAction = (action, event) => {
    // Fix S59 : preventDefault + stopPropagation pour éviter que le clic ne
    // remonte à un <form> parent ou ne déclenche une navigation d'ancre.
    if (event && event.preventDefault) event.preventDefault();
    if (event && event.stopPropagation) event.stopPropagation();
    if (action) action();  // action() d'abord, puis close (setIsOpen re-render unmount le bouton)
    setIsOpen(false);
  };

  // Icônes connecteurs par type
  const connectorIcons = {
    telegram: MessageCircle,
    email: Mail,
    gdrive: HardDrive,
    github: Github,
    n8n: Zap,
    site_internet: Globe,
    wordpress_source: Database,
  };

  // Connecteurs par défaut si pas encore chargés depuis l'API
  const defaultConnectors = [
    { type: 'telegram', name: 'Telegram', status: 'coming_soon' },
    { type: 'email', name: 'Email', status: 'coming_soon' },
    { type: 'gdrive', name: 'Google Drive', status: 'coming_soon' },
    { type: 'github', name: 'GitHub', status: 'coming_soon' },
    { type: 'n8n', name: 'N8N', status: 'available' },
  ];

  const displayConnectors = connectors.length > 0 ? connectors : (apiConnectors.length > 0 ? apiConnectors : defaultConnectors);

  return (
    <div ref={menuRef} style={{ position: 'relative', flexShrink: 0, marginRight: '8px', alignSelf: 'flex-end', marginBottom: '2px' }}>
      {/* Input fichier caché */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept=".txt,.md,.js,.jsx,.ts,.tsx,.py,.sql,.csv,.json,.html,.css,.xml,.yaml,.yml,.pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp,.svg"
      />

      {/* Bouton "+" */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          border: '1px solid var(--card-border)',
          background: isOpen ? 'var(--accent)' : 'var(--card-bg)',
          color: isOpen ? 'white' : 'var(--text-secondary)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
          opacity: disabled ? 0.5 : 1,
          boxShadow: isOpen ? '0 2px 8px rgba(29,78,216,0.3)' : 'none',
        }}
        onMouseEnter={e => { if (!disabled && !isOpen) { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; } }}
        onMouseLeave={e => { if (!isOpen) { e.currentTarget.style.borderColor = 'var(--card-border)'; e.currentTarget.style.color = 'var(--text-secondary)'; } }}
      >
        {isOpen ? <X size={16} /> : <Plus size={18} />}
      </button>

      {/* Popover menu */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '0',
          marginBottom: '8px',
          width: '260px',
          background: 'var(--modal-bg, var(--card-bg))',
          border: '1px solid var(--card-border)',
          borderRadius: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 200,
          overflow: 'hidden',
          animation: 'plusMenuSlideUp 0.15s ease-out',
        }}>

          {/* Section Fichiers */}
          <div style={{ padding: '8px 6px 4px' }}>
            <div style={{ padding: '2px 10px 6px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted, #6a6a85)' }}>
              Fichiers
            </div>
            <MenuItem icon={Paperclip} label="Ajouter des fichiers" onClick={handleFileClick} />
            <MenuItem icon={ClipboardPaste} label="Coller du contenu" onClick={() => handleAction(onPasteContent)} />
            <MenuItem icon={Camera} label="Capture d'écran" onClick={handleScreenCapture} sublabel="Ctrl+V" />
          </div>

          <Divider />

          {/* Section Projet (seulement en contexte projet) */}
          {isProjectContext && (
            <>
              <div style={{ padding: '8px 6px 4px' }}>
                <div style={{ padding: '2px 10px 6px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted, #6a6a85)' }}>
                  Projet
                </div>
                <MenuItem icon={Link} label="Lier un fichier existant" onClick={() => handleAction(onLinkFile)} />
                <MenuItem icon={StickyNote} label="Créer une note" onClick={() => handleAction(onCreateNote)} />
              </div>
              <Divider />
            </>
          )}

          {/* Section Connecteurs */}
          <div style={{ padding: '8px 6px 4px' }}>
            <div style={{ padding: '2px 10px 6px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted, #6a6a85)' }}>
              Connecteurs
            </div>
            {displayConnectors.map(c => {
              const Icon = connectorIcons[c.type] || Zap;
              const isAvailable = c.status === 'available';
              // S7 — clic en mode projet ouvre la modale d'activation
              // S2 — étendu à site_internet
              // (legacy callback onTriggerN8N reste pour mode général/hors projet)
              const CONNECTORS_WITH_MODAL = ['n8n', 'site_internet', 'email', 'wordpress_source'];
              const handleConnectorClick = () => {
                if (!isAvailable) return;
                // P7 — email actif : ouvre directement la boîte mail plein écran
                if (c.type === 'email' && Boolean(c.activated) && isProjectContext && projectId) {
                  setInboxConnector({ code: c.type, name: c.name });
                  setIsOpen(false);
                  return;
                }
                if (CONNECTORS_WITH_MODAL.includes(c.type) && isProjectContext && projectId) {
                  setActivatingConnector({
                    code: c.type,
                    name: c.name,
                    alreadyActive: Boolean(c.activated),
                  });
                  setIsOpen(false);
                  return;
                }
                if (c.type === 'n8n') handleAction(onTriggerN8N);
              };
              return (
                <MenuItem
                  key={c.type}
                  icon={Icon}
                  label={c.name}
                  disabled={!isAvailable}
                  badge={!isAvailable
                    ? 'Bientot'
                    : (CONNECTORS_WITH_MODAL.includes(c.type) && isProjectContext
                        ? (c.activated ? (c.type === 'email' ? 'Ouvrir' : 'Actif') : 'Activer')
                        : null)}
                  onClick={handleConnectorClick}
                />
              );
            })}
          </div>

          <Divider />

          {/* Section Outils */}
          <div style={{ padding: '8px 6px 8px' }}>
            <div style={{ padding: '2px 10px 6px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted, #6a6a85)' }}>
              Outils
            </div>
            {!showUrlInput ? (
              <MenuItem icon={Globe} label="Analyser un lien web" onClick={() => setShowUrlInput(true)} />
            ) : (
              <div style={{ padding: '4px 10px 8px' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="url"
                    value={urlValue}
                    onChange={e => setUrlValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && urlValue.match(/^https?:\/\//i)) {
                        if (onAnalyzeUrl) onAnalyzeUrl(urlValue);
                        setUrlValue(''); setShowUrlInput(false); setIsOpen(false);
                      }
                      if (e.key === 'Escape') { setShowUrlInput(false); setUrlValue(''); }
                    }}
                    placeholder="https://..."
                    autoFocus
                    style={{
                      flex: 1, padding: '6px 10px', borderRadius: 8, fontSize: 12,
                      border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                      color: 'var(--text)', outline: 'none',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (urlValue.match(/^https?:\/\//i) && onAnalyzeUrl) {
                        onAnalyzeUrl(urlValue);
                        setUrlValue(''); setShowUrlInput(false); setIsOpen(false);
                      }
                    }}
                    disabled={!urlValue.match(/^https?:\/\//i)}
                    style={{
                      padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                      border: 'none', cursor: 'pointer',
                      background: urlValue.match(/^https?:\/\//i) ? '#10b981' : 'var(--card-border)',
                      color: 'white', transition: 'all 0.15s',
                    }}
                  >OK</button>
                </div>
              </div>
            )}
            <MenuItem icon={Layout} label="Templates" onClick={() => handleAction(onInsertTemplate)} />
            {isProjectContext && onOrganizeFiles && (
              <MenuItem
                icon={FolderTree}
                label="Organiser les fichiers"
                sublabel="IA range le projet"
                onClick={(e) => handleAction(onOrganizeFiles, e)}
              />
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes plusMenuSlideUp {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* S7 — Modale d'activation de connecteur (rendue en portail visuel) */}
      {activatingConnector && projectId && (
        <ConnectorActivationModal
          projectId={projectId}
          connectorCode={activatingConnector.code}
          connectorName={activatingConnector.name}
          alreadyActive={activatingConnector.alreadyActive}
          onClose={() => setActivatingConnector(null)}
          onActivated={() => {
            setActivatingConnector(null);
            // S2 — refetch immédiat pour mettre le badge à jour (Actif/Activer)
            setRefreshTick(t => t + 1);
          }}
          onDeactivated={() => {
            setActivatingConnector(null);
            setRefreshTick(t => t + 1);
          }}
        />
      )}

      {/* P7 — Boîte mail plein écran (connecteur email actif) */}
      {inboxConnector && projectId && (
        <MailInbox
          projectId={projectId}
          connectorName={inboxConnector.name}
          onClose={() => setInboxConnector(null)}
          onOpenSettings={() => {
            setInboxConnector(null);
            setActivatingConnector({ code: 'email', name: inboxConnector.name, alreadyActive: true });
          }}
        />
      )}
    </div>
  );
};

// ---- Sous-composants ----

const MenuItem = ({ icon: Icon, label, sublabel, onClick, disabled = false, badge }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      width: '100%',
      padding: '8px 10px',
      border: 'none',
      borderRadius: '8px',
      background: 'transparent',
      color: disabled ? 'var(--text-muted, #6a6a85)' : 'var(--text)',
      fontSize: '13px',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'background 0.15s',
      textAlign: 'left',
    }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = 'var(--accent-light, rgba(29,78,216,0.08))'; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
  >
    <Icon size={16} style={{ flexShrink: 0, opacity: disabled ? 0.5 : 0.8 }} />
    <span style={{ flex: 1 }}>{label}</span>
    {sublabel && !badge && (
      <span style={{ fontSize: '10px', color: 'var(--text-muted, #6a6a85)', opacity: 0.7 }}>{sublabel}</span>
    )}
    {badge && (
      <span style={{
        fontSize: '9px',
        fontWeight: '600',
        padding: '1px 6px',
        borderRadius: '8px',
        background: 'var(--accent-light, rgba(29,78,216,0.1))',
        color: 'var(--accent, #1D4ED8)',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}>
        {badge}
      </span>
    )}
  </button>
);

const Divider = () => (
  <div style={{ height: '1px', background: 'var(--card-border)', margin: '0 10px' }} />
);

export default PlusMenu;
